import React, { useState, useEffect } from 'react';
import { Modal } from '../../components/UI/Modal';
import { Input } from '../../components/UI/Input';
import { Button } from '../../components/UI/Button';
import { useStore } from '../../store/StoreContext';
import { generateId } from '../../utils/helpers';
import SafeIcon from '../../common/SafeIcon';

const CATEGORY_ICONS = [
  'Code', 'Layers', 'Monitor', 'Settings', 'Database', 'Globe', 'Zap', 'Cpu', 'Edit3', 'Briefcase', 'Terminal', 'Shield', 'Cloud', 'Smartphone', 'Search'
];

export const CategoryModal = ({ isOpen, onClose, editingCategory = null, categoryType = 'task' }) => {
  const { addEntity, updateEntity, deleteEntity } = useStore();
  const [formData, setFormData] = useState({ name: '', icon: 'Layers' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editingCategory) {
        setFormData({ name: editingCategory.name || '', icon: editingCategory.icon || 'Layers' });
      } else {
        setFormData({ name: '', icon: 'Layers' });
      }
    }
  }, [editingCategory, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = { ...formData, type: editingCategory?.type || categoryType };
    try {
      if (editingCategory) {
        await updateEntity('categories', { ...data, id: editingCategory.id });
      } else {
        await addEntity('categories', { ...data, id: generateId('cat') });
      }
      onClose();
    } catch (err) {
      console.error("Failed to save category:", err);
      alert(`Error saving category: ${err.message || 'Check your connection'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editingCategory ? `Edit ${categoryType} Category` : `New ${categoryType} Category`}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input 
          label="Category Name" 
          value={formData.name} 
          onChange={e => setFormData({ ...formData, name: e.target.value })} 
          required 
          placeholder={categoryType === 'task' ? "e.g., Research, Design, Dev..." : "e.g., Onboarding, Maintenance..."}
          disabled={saving}
        />

        <div className="space-y-3">
          <label className="text-xs font-medium text-gray-400">Select Icon</label>
          <div className="grid grid-cols-5 gap-3">
            {CATEGORY_ICONS.map(iconName => (
              <button
                key={iconName}
                type="button"
                disabled={saving}
                onClick={() => setFormData({ ...formData, icon: iconName })}
                className={`flex items-center justify-center p-4 rounded-xl border transition-all ${
                  formData.icon === iconName 
                    ? 'bg-primary/20 border-primary text-primary shadow-lg shadow-primary/10' 
                    : 'bg-base-900 border-base-700 text-gray-500 hover:border-base-600 hover:text-gray-300'
                }`}
              >
                <SafeIcon name={iconName} className="w-6 h-6" />
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-base-700">
          {editingCategory && (
            <Button 
              variant="danger" 
              type="button" 
              disabled={saving}
              onClick={async () => {
                if(confirm('Delete this category?')) {
                  try {
                    await deleteEntity('categories', editingCategory.id);
                    onClose();
                  } catch (err) {
                    alert(err.message);
                  }
                }
              }} 
            >
              Delete
            </Button>
          )}
          <Button variant="ghost" type="button" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : editingCategory ? "Update Category" : "Create Category"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};